import { makeStyles } from "tss-react/mui"
import { StandardTextField } from "../../../../forms/StandardTextField"
import { Box, FormHelperText, TextField } from "@mui/material"
import { faDollarSign, faFileLines } from "@fortawesome/pro-regular-svg-icons"
import { useFormikContext } from "formik"

import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { toDinero } from "../../../../../../modules/currency/currencyLib"
import { useMoney } from "../../../../../../modules/currency/useMoney"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { StandardAlertInfo } from "../../../../alert/StandardAlertInfo"
import { SelectableRadioCardSkeleton } from "../../../../selectableRadioCard/SelectableRadioCard"
import { PaymentMethodType } from "../../../../../../modules/payments/paymentTypes"

import type { TakeawayPaymentMethod } from "../takeawayTypes"
import type { CheckoutFormValues } from "./takeawayCheckoutLib"
import { isValidPaymentMethod, toEnumKey } from "./takeawayCheckoutLib"
import { TakeawayCheckoutSectionHeader } from "./TakeawayCheckoutSectionHeader"
import { TakeawayPaymentOption } from "./TakeawayPaymentOption"

function buildCardName(
    card: NonNullable<TakeawayPaymentMethod["card"]>,
    l10n: ReturnType<typeof useAppLocalization>["l10n"],
): string {
    const cardInfo =
        card.displayName ??
        `${card.type ?? ""} **** ${card.number ?? ""}${card.expiryDate ? ` (${card.expiryDate})` : ""}`.trim()
    return l10n.getString("takeaway-confirm-order-pay-using-card", { card: cardInfo })
}

function buildWalletName(
    method: TakeawayPaymentMethod,
    walletBalance: ReturnType<typeof toDinero>,
    moneyFactory: ReturnType<typeof useMoney>,
    l10n: ReturnType<typeof useAppLocalization>["l10n"],
): string {
    const baseName = l10n.getStringForEnum("purchases-payment-method", toEnumKey(method.value), method.value)
    return l10n.getString("takeaway-confirm-order-wallet-name-with-balance", {
        name: baseName,
        balance: moneyFactory.format(walletBalance),
    })
}

function buildNameOverride(
    method: TakeawayPaymentMethod,
    walletBalance: ReturnType<typeof toDinero> | undefined,
    moneyFactory: ReturnType<typeof useMoney>,
    l10n: ReturnType<typeof useAppLocalization>["l10n"],
): string | undefined {
    if (method.card) return buildCardName(method.card, l10n)
    if (method.value === PaymentMethodType.GOPAY_WALLET && walletBalance) {
        return buildWalletName(method, walletBalance, moneyFactory, l10n)
    }
    return undefined
}

const useStyles = makeStyles()((theme) => ({
    paymentList: {
        display: "flex",
        flexDirection: "column",
        gap: ".75rem",
    },
    paymentError: {
        margin: ".25rem 0 0",
    },
    methodWrap: {
        display: "flex",
        flexDirection: "column",
        gap: ".75rem",
    },
    noticeWrap: {
        padding: "0 1rem .875rem",
    },
    noticeAlert: {
        width: "100%",
        background: "#2196F3",
        "& .MuiAlert-icon": {
            color: theme.palette.common.white,
        },
        "& .MuiAlert-message, & .MuiAlert-message *": {
            color: theme.palette.common.white,
            fontSize: "1rem",
        },
    },
}))

const SKELETON_COUNT = 3

function TakeawayPaymentMethodSkeleton() {
    return (
        <>
            {Array.from({ length: SKELETON_COUNT }, (_, i) => (
                <SelectableRadioCardSkeleton key={i} hasEndAdornment />
            ))}
        </>
    )
}

export type TakeawayCheckoutPaymentSectionProps = Readonly<{
    isLoading: boolean
    methods: ReadonlyArray<TakeawayPaymentMethod>
    selectedMethodValue: string
    walletTotal: ReturnType<typeof toDinero> | undefined
    walletBalance: ReturnType<typeof toDinero> | undefined
    moneyFactory: ReturnType<typeof useMoney>
    accountingValues: Record<string, string>
    notice?: string
    accountingDimensions?: TakeawayPaymentMethod["accounting"]
    onMethodSelect: (value: string) => void
    onAccountingChange: (name: string, value: string) => void
}>

export function TakeawayCheckoutPaymentSection({
    isLoading,
    methods,
    selectedMethodValue,
    walletTotal,
    walletBalance,
    moneyFactory,
    accountingValues,
    notice,
    accountingDimensions,
    onMethodSelect,
    onAccountingChange,
}: TakeawayCheckoutPaymentSectionProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const { errors, submitCount } = useFormikContext<CheckoutFormValues>()
    const showPaymentError = submitCount > 0 && Boolean(errors.selectedMethodValue)

    return (
        <>
            <Box>
                <TakeawayCheckoutSectionHeader
                    icon={faDollarSign}
                    title={
                        <LocalizedStrict id="takeaway-confirm-order-payment-method-title">
                            <span>Payment Method</span>
                        </LocalizedStrict>
                    }
                />
                <Box className={classes.paymentList}>
                    {isLoading ? (
                        <TakeawayPaymentMethodSkeleton />
                    ) : (
                        methods.map((method) => {
                            const showNotice =
                                selectedMethodValue === method.value &&
                                (notice !== undefined || method.value === PaymentMethodType.MOBILE_PAY)
                            const noticeText = notice ?? l10n.getString("takeaway-payment-mobilepay-notice")
                            return (
                                <Box key={method.value} className={classes.methodWrap}>
                                    <TakeawayPaymentOption
                                        method={method}
                                        isSelected={selectedMethodValue === method.value}
                                        isDisabled={!isValidPaymentMethod(method, walletTotal, walletBalance)}
                                        nameOverride={buildNameOverride(method, walletBalance, moneyFactory, l10n)}
                                        onClick={() => onMethodSelect(method.value)}
                                    />
                                    {showNotice && (
                                        <Box className={classes.noticeWrap}>
                                            <StandardAlertInfo title={noticeText} className={classes.noticeAlert} />
                                        </Box>
                                    )}
                                </Box>
                            )
                        })
                    )}
                </Box>
                {showPaymentError && (
                    <FormHelperText error className={classes.paymentError}>
                        {errors.selectedMethodValue}
                    </FormHelperText>
                )}
            </Box>

            {accountingDimensions?.dimensions && accountingDimensions.dimensions.length > 0 && (
                <Box>
                    <TakeawayCheckoutSectionHeader
                        icon={faFileLines}
                        title={
                            <LocalizedStrict id="takeaway-confirm-order-accounting-title">
                                <span>Accounting</span>
                            </LocalizedStrict>
                        }
                    />
                    {accountingDimensions.dimensions.map((dim) => (
                        <StandardTextField
                            key={dim.id}
                            fullWidth
                            size="small"
                            label={dim.inputField.label ?? dim.inputField.name}
                            value={accountingValues[dim.inputField.name] ?? ""}
                            onChange={(e) => onAccountingChange(dim.inputField.name, e.target.value)}
                        />
                    ))}
                </Box>
            )}
        </>
    )
}
